<template>
  <v-container class="px-6 px-sm-12">
    <PageHeader
      :header-text="'Per Day Tank Status Report'"
      :header-icon="'mdi-car-coolant-level'"
    />
    <div class="d-flex justify-space-between">
      <div class="d-flex align-end">
        <v-icon left large color="primary">mdi-calendar</v-icon>
        <DatePicker
          :currentDate="date"
          :maxDate="dateNow"
          v-on:dateChanged="dateChanged"
        />
      </div>
      <ExcelDownloadButton
        :size="headerButtonSize"
        :onClick="exportAsExcel"
        :isLoading="isExportingExcel"
      />
    </div>
    <div v-if="loading">
      <v-progress-circular color="primary" indeterminate> </v-progress-circular>
    </div>
    <div v-else>
      <DailyReportSummary
        :dailyReport="dailyTankReport"
        :values="summaryValues"
      />
      <v-tabs
        dark
        background-color="primary"
        v-model="tab"
        :show-arrows="showTabArrows"
      >
        <v-tabs-slider color="primary" />
        <v-tab href="#open-close-recordings">Open/Close Recordings</v-tab>
        <v-tab href="#recordings">Recordings</v-tab>
        <v-tab href="#unrecorded-tanks" v-if="showUnrecordedTanksTab"
          >Unrecorded Tanks
          <v-chip class="mx-1 pa-2" color="warning" small>{{
            unrecordedTanks.length
          }}</v-chip>
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="open-close-recordings">
          <DailyTankReportOpenCloseStatusTable :items="openCloseRecordings" />
        </v-tab-item>
        <v-tab-item value="recordings">
          <div v-if="vesselsWithTankRecordings.length > 0">
            <v-card
              class="mb-6"
              v-for="item in vesselsWithTankRecordings"
              :key="item.vessel.id"
            >
              <DailyTankReportRecordingsTable
                :title="item.vessel.name"
                :tankRecordings="item.tankRecordings"
              />
            </v-card>
          </div>
          <v-row v-else justify="center">
            <InfoAlert message="No Recordings Found" />
          </v-row>
        </v-tab-item>
        <v-tab-item value="unrecorded-tanks">
          <v-card>
            <DailyTankReportTanksTable :tanks="unrecordedTanks" />
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </v-container>
</template>

<script>
import Vue from 'vue';
import { mapState, mapGetters } from 'vuex';
import ResponsiveSizeHelperMixin from '../mixins/ResponsiveSizeHelperMixin';
import PageHeader from '../components/ui/PageHeader.vue';
import DatePicker from '../components/ui/DatePicker.vue';
import ExcelDownloadButton from '../components/ui/ExcelDownloadButton.vue';
import DownloadHelperMixin from '../mixins/DownloadHelperMixin';
import TankStatusMixin from '../mixins/TankStatusMixin';
import DailyReportSummary from '../components/daily-reports/DailyReportSummary.vue';
import DailyTankReportRecordingsTable from '../components/daily-reports/DailyTankReportRecordingsTable.vue';
import DailyTankReportTanksTable from '../components/daily-reports/DailyTankReportTanksTable.vue';
import InfoAlert from '../components/ui/InfoAlert.vue';
import DailyTankReportOpenCloseStatusTable from '../components/daily-reports/DailyTankReportOpenCloseStatusTable.vue';

export default {
  name: 'PerDayTankStatusReport',
  components: {
    PageHeader,
    DatePicker,
    ExcelDownloadButton,
    DailyReportSummary,
    DailyTankReportRecordingsTable,
    DailyTankReportTanksTable,
    InfoAlert,
    DailyTankReportOpenCloseStatusTable,
  },
  mixins: [ResponsiveSizeHelperMixin, TankStatusMixin, DownloadHelperMixin],
  data() {
    return {
      loading: false,
      isExportingExcel: false,
      date: null,
      dateNow: new Date().toISOString().substr(0, 10),
    };
  },
  computed: {
    ...mapState(['voyageReportsStore', 'userStore']),
    ...mapGetters({
      getActiveSite: 'userStore/getActiveSite',
      getDailyTankReport: 'dailyReportsStore/getDailyTankReport',
    }),
    tab: {
      set(tab) {
        this.$router.replace({ query: { ...this.$route.query, tab } });
      },
      get() {
        return this.$route.query.tab;
      },
    },
    activeSiteId() {
      const site = this.getActiveSite;
      if (!site) return null;
      return site.id;
    },
    dailyTankReport() {
      return this.getDailyTankReport;
    },
    vesselsWithTankRecordings() {
      return this.dailyTankReport.vesselsWithTankRecordings;
    },
    openCloseRecordings() {
      return this.dailyTankReport.openCloseRecordings;
    },
    unrecordedTanks() {
      return this.dailyTankReport.unrecordedTanks;
    },
    showUnrecordedTanksTab() {
      return this.unrecordedTanks.length > 0;
    },
    summaryValues() {
      const valuesArray = [
        {
          value: this.dailyTankReport.recordingsCount, prevDayValue: this.dailyTankReport.recordingsCountForPreviousDay, label: 'Recordings', icon: 'mdi-list-status',
        },
        {
          value: this.dailyTankReport.tanksRecordedCount, prevDayValue: this.dailyTankReport.tanksRecordedCountForPreviousDay, label: 'Tanks Recorded', icon: 'mdi-car-coolant-level',
        },
        {
          value: this.dailyTankReport.tanksUnrecordedCount, prevDayValue: this.dailyTankReport.tanksUnrecordedCountForPreviousDay, label: 'Unrecorded Tanks', icon: 'mdi-alert-circle',
        },
      ];
      return valuesArray;
    },
  },
  watch: {
    activeSiteId(newVal) {
      this.loadDailyTankReport(this.date);
    },
  },
  created() {
    this.date = this.dateNow;

    const { date } = this.$route.params;
    if (date) this.date = date;

    this.loadDailyTankReport(this.date);
  },
  methods: {
    loadDailyTankReport(date) {
      this.loading = true;
      this.$store.dispatch('dailyReportsStore/loadDailyTankReport', { activeSiteId: this.activeSiteId, date })
        .finally(() => {
          this.loading = false;
        });
    },
    dateChanged(newDate) {
      this.date = newDate;
      this.loadDailyTankReport(newDate);
    },
    exportAsExcel() {
      this.isExportingExcel = true;
      this.downloadFile('dailyReportsStore/getDailyTankReportAsExcelFile', { activeSiteId: this.activeSiteId, date: this.date })
        .finally(() => { this.isExportingExcel = false; });
    },
  },
};
</script>

<style scoped>
</style>
